import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { SITE } from '@/constants/site';

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/topics?q=${encodeURIComponent(value)}`);
    setKeyword('');
    setSearchOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm transition-colors ${
      isActive
        ? 'text-primary-600 font-medium'
        : 'text-foreground-600 hover:text-foreground-950'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label={`${SITE.name} 首页`}>
          <span className="flex h-9 w-9 items-center justify-center bg-primary-500 text-background-50">
            <i className="ri-radar-line text-lg" aria-hidden="true" />
          </span>
          <span className="leading-none">
            <span className="block font-heading text-lg font-bold tracking-tight text-foreground-950">
              {SITE.name}
            </span>
            <span className="hidden text-[10px] uppercase tracking-[0.2em] text-foreground-500 sm:block">
              {SITE.latinName}
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center md:flex" aria-label="主导航">
          {SITE.nav.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === '/'}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center lg:flex">
            {searchOpen ? (
              <div className="flex h-9 items-center gap-1 border border-background-300 bg-background-100 px-2">
                <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true" />
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索话题关键词"
                  aria-label="搜索话题关键词"
                  className="w-36 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none xl:w-48"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap text-xs text-primary-600 hover:text-primary-700"
                >
                  搜索
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="打开搜索"
                className="flex h-9 w-9 cursor-pointer items-center justify-center text-foreground-600 transition-colors hover:text-primary-600"
              >
                <i className="ri-search-line text-lg" aria-hidden="true" />
              </button>
            )}
          </form>

          <Link
            to="/contact"
            className="hidden whitespace-nowrap bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-block"
          >
            投稿线索
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="展开导航菜单"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center text-foreground-700 md:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 md:hidden">
          <nav className="flex flex-col py-2" aria-label="移动端导航">
            {SITE.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `border-b border-background-200/70 py-3 text-sm ${
                    isActive ? 'text-primary-600 font-medium' : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="mt-2 flex items-center gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索话题关键词"
              aria-label="搜索话题关键词"
              className="w-full border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <button
              type="submit"
              aria-label="搜索"
              className="flex h-10 w-10 shrink-0 items-center justify-center bg-primary-500 text-background-50"
            >
              <i className="ri-search-line" aria-hidden="true" />
            </button>
          </form>
          <Link
            to="/contact"
            className="mt-3 block bg-primary-500 px-4 py-2.5 text-center text-sm font-medium text-background-50"
          >
            投稿线索
          </Link>
        </div>
      )}
    </header>
  );
}